import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { healthCyclesAPI, bloodTestsAPI } from '../../services/api'
import AIBodyAnalysis from '../profile/AIBodyAnalysis'
import { useStartHealthCycle, cyclePath } from './useStartHealthCycle'
import { formatDate } from './cycleStatus'
import CycleStateCard from './CycleStateCard'
import UntilNextTestSection from '../progress/UntilNextTestSection'

const STATUS_BADGE = { finalized: 'badge-success', draft: 'badge-warning', running: 'badge-primary' }

function StartCycleForm({ initialTestId = '', bare = false }) {
  const { t, i18n } = useTranslation()
  const [bloodTestId, setBloodTestId] = useState(initialTestId ? String(initialTestId) : '')
  const [questions, setQuestions] = useState('')
  const { start, discardAndRestart, openExisting, conflict, isPending, error } = useStartHealthCycle()
  const { data: tests = [] } = useQuery({
    queryKey: ['bloodTests'],
    queryFn: () => bloodTestsAPI.getAll(),
    select: (response) => (Array.isArray(response.data) ? response.data : response.data?.items ?? []),
  })
  const analysed = tests.filter((test) => test.status === 'analyzed')

  const submit = (e) => {
    e.preventDefault()
    if (!bloodTestId) return
    start({
      bloodTestId: Number(bloodTestId),
      questions: questions.split('\n').map((q) => q.trim()).filter(Boolean).slice(0, 10),
    })
  }

  return (
    <form onSubmit={submit} className={bare ? '' : 'card'} data-testid="start-cycle-form">
      <div className={bare ? 'space-y-3' : 'card-body space-y-3'}>
        {!bare && <h3 className="text-sm font-semibold text-gray-900">{t('healthCycle.start.title')}</h3>}
        <label className="block text-sm text-gray-700">
          {t('healthCycle.start.bloodTest')}
          <select
            className="mt-1 block w-full text-sm"
            value={bloodTestId}
            onChange={(e) => setBloodTestId(e.target.value)}
          >
            <option value="">{t('healthCycle.start.pickTest')}</option>
            {analysed.map((test) => (
              <option key={test.id} value={test.id}>
                {formatDate(test.test_date, i18n.language)}
                {test.lab_name ? ` — ${test.lab_name}` : ''}
              </option>
            ))}
          </select>
        </label>
        <label className="block text-sm text-gray-700">
          {t('healthCycle.start.questions')}
          <textarea
            className="mt-1 block w-full text-sm"
            rows={2}
            value={questions}
            onChange={(e) => setQuestions(e.target.value)}
            placeholder={t('healthCycle.start.questionsPlaceholder')}
          />
        </label>
        {error && <p className="text-xs text-red-600" role="alert">{t(`healthCycle.errors.${error}`, t('healthCycle.errors.generic'))}</p>}
        {conflict && (
          <div className="border border-yellow-200 bg-yellow-50 rounded-md p-3 text-sm">
            <p className="text-yellow-800">{t('healthCycle.start.draftExists')}</p>
            <div className="flex gap-2 mt-2">
              <button type="button" className="btn-outline text-xs" onClick={openExisting}>{t('healthCycle.start.openDraft')}</button>
              <button type="button" className="btn-outline text-xs" onClick={discardAndRestart}>{t('healthCycle.start.discardDraft')}</button>
            </div>
          </div>
        )}
        <button type="submit" className="btn-primary text-sm" disabled={!bloodTestId || isPending}>
          {isPending ? t('healthCycle.start.starting') : t('healthCycle.start.button')}
        </button>
      </div>
    </form>
  )
}

// VH-227: replaces the "AI Health Analysis" section — cycles by blood test date,
// with earlier comprehensive analyses still readable below.
export function HealthCyclePanel() {
  const { t, i18n } = useTranslation()
  const [showEarlier, setShowEarlier] = useState(false)
  const [showStartOther, setShowStartOther] = useState(false)
  const { data: cycles = [], isLoading } = useQuery({
    queryKey: ['healthCycles'],
    queryFn: () => healthCyclesAPI.list().then((response) => response.data ?? []),
  })
  // VH-247 stage 1: lead with where the user stands, not with "Start a health cycle"
  const stateQuery = useQuery({
    queryKey: ['healthCycleState'],
    queryFn: () => healthCyclesAPI.currentState().then((response) => response.data),
  })
  const state = stateQuery.data
  const startLeads = state?.state === 'reviewing' && state.review === 'not_started'
  const agreedInForce = state && (state.state === 'doing' || state.state === 'measuring')

  return (
    <div className="space-y-6">
      {stateQuery.isLoading && <p className="text-sm text-gray-500">{t('common.loading')}</p>}
      {stateQuery.isError && (
        <div className="card">
          <div className="card-body text-sm">
            <p className="text-red-600">{t('healthCycle.state.loadError')}</p>
            <button type="button" className="btn-outline mt-2 text-xs" onClick={() => stateQuery.refetch()}>
              {t('healthCycle.state.retry')}
            </button>
          </div>
        </div>
      )}
      {state && (
        <CycleStateCard
          state={state}
          onStartWaiting={startLeads ? <StartCycleForm bare initialTestId={state.waiting_test.blood_test_id} /> : null}
        />
      )}
      {agreedInForce && <UntilNextTestSection />}

      {/* Starting a cycle leads only when a new test waits; otherwise it is a secondary action.
          If the state could not be read, the form shows as before so nothing is blocked. */}
      {stateQuery.isError && <StartCycleForm />}
      {state && !startLeads && state.state !== 'none' && (
        <div>
          <button
            type="button"
            className="text-sm font-medium text-primary-600 hover:text-primary-700"
            onClick={() => setShowStartOther((v) => !v)}
            aria-expanded={showStartOther}
          >
            {showStartOther ? t('healthCycle.state.hideStartOther') : t('healthCycle.state.startOther')}
          </button>
          {showStartOther && <div className="mt-3"><StartCycleForm /></div>}
        </div>
      )}

      <section aria-label={t('healthCycle.list.title')}>
        <h2 className="text-lg font-semibold text-gray-900 mb-3">{t('healthCycle.list.title')}</h2>
        {isLoading ? (
          <p className="text-sm text-gray-500">{t('common.loading')}</p>
        ) : cycles.length === 0 ? (
          <p className="text-sm text-gray-500">{t('healthCycle.list.empty')}</p>
        ) : (
          <ul className="grid gap-3">
            {cycles.map((cycle) => (
              <li key={cycle.id} className="card">
                <Link to={cyclePath(cycle.id)} className="card-body flex items-center justify-between gap-3">
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      {t('healthCycle.list.bloodTestOf', { date: formatDate(cycle.blood_test_date, i18n.language) })}
                    </p>
                    <p className="text-xs text-gray-500">
                      {t('healthCycle.list.versions', { count: cycle.version_count })}
                      {cycle.finalized_at && ` · ${t('healthCycle.list.finalizedOn', { date: formatDate(cycle.finalized_at, i18n.language) })}`}
                    </p>
                  </div>
                  <span className={`badge ${STATUS_BADGE[cycle.status] || 'badge-primary'}`}>
                    {t(`healthCycle.status.${cycle.status}`, cycle.status)}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section aria-label={t('healthCycle.earlier.title')}>
        <button
          type="button"
          className="text-sm font-medium text-primary-600 hover:text-primary-700"
          onClick={() => setShowEarlier((v) => !v)}
          aria-expanded={showEarlier}
        >
          {showEarlier ? t('healthCycle.earlier.hide') : t('healthCycle.earlier.show')}
        </button>
        <p className="text-xs text-gray-500 mt-1">{t('healthCycle.earlier.description')}</p>
        {showEarlier && (
          <div className="mt-4">
            <AIBodyAnalysis />
          </div>
        )}
      </section>
    </div>
  )
}

export default HealthCyclePanel
